'use strict';
/* ==========================================================================
   core-local/civics.js — "Nobody Gets All the Power": the civics model (DESIGN.md §4). Pure: no DOM, no engine
   globals. Runs in node (module.exports: tools/civicscheck.js, tools/contentcheck.js) and in the page
   (window.Civics). Registers itself with the question bank as `civics` when core/bank.js is loaded first (lesson.json
   scripts order), so a pool expression may say civics.passes(hy, hn) or civics.twoThirdsNeeded(ohv).

   EVERY vote threshold, bill outcome and "allowed / not allowed" verdict in the lesson comes from here (the honesty
   rule): the games never type one in. Integers only. "Voting" = members present and voting (yes + no); absent and
   "present" members never count (facts-branches.md Corrections #3 and #5).

   ---- vote maths ----
   Civics.CHAMBERS                    {house: 435, senate: 100}   (facts 3.1, 3.5)
   Civics.QUORUM                      {house: 218, senate: 51}    (facts 3.7; a majority of the members)
   Civics.VETOES                      {total 2599, regular 1533, pocket 1066, overridden 112, since 1789, read, cite} (facts 4.7)
   Civics.majorityNeeded(v)           ⌊v/2⌋ + 1 yes votes pass a bill when all v vote     (facts 4.1)
   Civics.passes(yes, no)             yes > no (a tie fails)                              (facts 4.1)
   Civics.fails(yes, no)              !passes
   Civics.tie(yes, no)                yes === no
   Civics.senatePasses(yes, no, vp)   yes > no, or a tie broken by the VP voting yes      (facts 4.1, 5.4)
   Civics.twoThirdsNeeded(v)          ⌈2v/3⌉ (exact integer maths; exactly two-thirds is enough: 290 of 435)  (facts 4.5, 4.6)
   Civics.overrides(yes, no)          3·yes ≥ 2·(yes + no), someone voting                (facts 4.5, 4.6)
   Civics.overrideFails(yes, no)      !overrides
   Civics.twoThirds(yes, no)          = overrides (the same rule: conviction 7.2, treaties 8.1, amendments 8.3, expulsion 8.7)

   ---- a bill's honest journey ----
   Civics.journey(bill, params)  → {stages: [{stage, yes, no, result, rule, cite}], path: ['house:pass', …], outcome}
      bill = a bank.bill.json item ({president: 'sign' | 'veto' | 'wait-in-session' | 'wait-adjourned', vp?: 'yes' |
      'no', court?: 'struck' | 'upheld', origin?: 'house' | 'senate', tags}), params = its sampled params (hv/hy/hn,
      sv/sy/sn, ohy/ohn, osy/osn). house → senate → president → [override-house → override-senate] → law | dead
      (→ court:struck). A bill dies at the first failed stage; a pocket veto can't be overridden (4.4); a revenue bill
      (tag "revenue") must start in the House (4.2). Path strings: 'house:pass|fail', 'senate:pass|fail|tie-vp-yes|
      tie-vp-no', 'president:sign|veto|wait-in-session|wait-adjourned', 'override-house:pass|fail',
      'override-senate:pass|fail', 'law', 'dead', 'court:struck|upheld'. Throws on impossible votes (more voters than
      seats, negative counts, no quorum).

   ---- the powers table ----
   Civics.allowed(claim, params?) → {ok, rule, cite, text}   claim = {actor, act, ...} (bank.front.json); a string
      value naming a param is read from `params` (Civics.resolve). Actors: house, senate, congress, president, vp,
      court, senator, representative. An unknown actor/act → {ok: false, rule: 'unknown', unknown: true} (the check
      treats that as a content error, never as a proof).
   Civics.RULES[rule] = {text, cite}   the rule each verdict rests on (cite = facts-branches.md row + Constitution)
   Civics.CHEATS[cheatName] = rule     the rule each Front Page cheat must break (tools/civicscheck.js proves it)

   ---- the shared code path for the games and the checks (uses core/bank.js: its sampler, evaluator and filler) ----
   Civics.bill(item, r, {slots})       one bill instance from a bank.bill.json item: {id, key, title, level, params,
                                       journey, asks: [bank instances + {at, ask, truth}]}; r = Bank.rng(seed) (or a seed)
   Civics.drawBills(json, {seed, avoid}) → {seed, bills}   draw.n bills, every draw.require tag covered, fresh items
                                       first (avoid = last attempt's ids), served easiest first (level)
   Civics.headline(item, r)            one Front Page headline: {id, key, kind, cheat, level, params, headline, small,
                                       claim (resolved), verdict: allowed(claim), card (cheat: the "What rule does it
                                       break?" bank instance), fix}
   Civics.drawFront(json, {seed, avoid}) → {seed, pages: [{cheat: headline | null, headlines: [3], answer}], cheats,
                                       legit}   5 unique cheats (easier first) + 13 legit; one all-legit page; two
                                       headlines from the same template never share a page
   Civics.askItem(bill, ask, i)        the bank item an ask becomes (template or scenario item): tools/contentcheck.js
                                       runs tools/bankcheck.js on these
   ========================================================================== */
(function (root) {
  const B = () => root.Bank || (typeof require === 'function' ? tryRequireBank() : null);
  function tryRequireBank() {
    try { return require('../../../shared/engine/v2/core/bank.js'); } catch (e) { return null; }
  }

  /* ---------------- facts (docs/topics/facts-branches.md rows) ---------------- */
  const CHAMBERS = Object.freeze({ house: 435, senate: 100 });
  const QUORUM = Object.freeze({ house: 218, senate: 51 });
  const AGE = Object.freeze({ representative: 25, senator: 30, president: 35 });
  const TERM = Object.freeze({ house: 2, senate: 6, president: 4 });
  const MAX_PRESIDENT_TERMS = 2;
  /* the real veto record for the video's data card (facts 4.7, senate.gov, read 2026-09-28; tools/civicscheck.js
     compares it with the facts file; re-check the live table before shipping) */
  const VETOES = Object.freeze({ total: 2599, regular: 1533, pocket: 1066, overridden: 112, since: 1789, read: '2026-09-28',
    source: 'senate.gov/legislative/vetoes/vetoCounts.htm', cite: 'facts-branches.md 4.7' });

  const RULES = {
    lawmaking:         { text: 'Only Congress makes laws. Courts decide cases; the President signs or vetoes.', cite: 'facts-branches.md 2.1, 9.1, 9.2 (Art. I, Sec. 1)' },
    majority:          { text: 'A bill needs more yes votes than no votes in each chamber.', cite: 'facts-branches.md 4.1' },
    'both-chambers':   { text: 'Both chambers must pass the same bill before it goes to the President.', cite: 'facts-branches.md 4.1, 4.3 (Art. I, Sec. 7)' },
    'vp-tie':          { text: 'The Vice President votes in the Senate only to break a tie.', cite: 'facts-branches.md 5.4, 9.6 (Art. I, Sec. 3)' },
    veto:              { text: 'The President signs a bill or vetoes it.', cite: 'facts-branches.md 4.3 (Art. I, Sec. 7)' },
    'ten-days':        { text: 'Ten days (Sundays excepted) with Congress in session: the bill becomes law without a signature.', cite: 'facts-branches.md 4.4 (Art. I, Sec. 7)' },
    'pocket-veto':     { text: 'If Congress has adjourned, doing nothing kills the bill: a pocket veto, which cannot be overridden.', cite: 'facts-branches.md 4.4, Corrections #10 (Art. I, Sec. 7)' },
    'two-thirds-override': { text: 'Overriding a veto takes two-thirds of those voting in each chamber (exactly two-thirds is enough).', cite: 'facts-branches.md 4.5, 4.6 (Art. I, Sec. 7)' },
    'revenue-house':   { text: 'Bills for raising revenue start in the House.', cite: 'facts-branches.md 4.2 (Art. I, Sec. 7)' },
    'judicial-review': { text: 'Only the courts, in a real case, decide that a law breaks the Constitution.', cite: 'facts-branches.md 6.1, 9.3 (Marbury v. Madison, 1803)' },
    'real-case':       { text: 'Courts decide only real cases someone brings to them; no advisory opinions.', cite: 'facts-branches.md 6.2 (Art. III, Sec. 2)' },
    'life-tenure':     { text: 'Federal judges serve during good behaviour; only impeachment and a Senate conviction remove one.', cite: 'facts-branches.md 6.4, 9.4 (Art. III, Sec. 1)' },
    nominate:          { text: 'The President nominates judges and top officials.', cite: 'facts-branches.md 5.7, 6.5 (Art. II, Sec. 2)' },
    'senate-confirms': { text: 'The Senate confirms the President\'s nominees (a simple majority); the House has no vote.', cite: 'facts-branches.md 5.7, 6.5 (Art. II, Sec. 2)' },
    'house-impeaches': { text: 'The House impeaches (a simple majority); the Senate holds the trial.', cite: 'facts-branches.md 7.1 (Art. I, Sec. 2)' },
    'senate-tries':    { text: 'The Senate tries impeachments; convicting takes two-thirds of the senators present.', cite: 'facts-branches.md 7.2 (Art. I, Sec. 3)' },
    war:               { text: 'Only Congress can declare war; the President commands the military.', cite: 'facts-branches.md 5.5 (Art. I, Sec. 8; Art. II, Sec. 2)' },
    commander:         { text: 'The President is Commander in Chief of the armed forces.', cite: 'facts-branches.md 5.5 (Art. II, Sec. 2)' },
    purse:             { text: 'Money is spent only by laws Congress passes: the power of the purse.', cite: 'facts-branches.md 8.2 (Art. I, Sec. 9)' },
    treaty:            { text: 'The Senate approves treaties with two-thirds of the senators present (advice and consent).', cite: 'facts-branches.md 8.1, Corrections #2 (Art. II, Sec. 2)' },
    amendment:         { text: 'Changing the Constitution (or undoing a ruling on it) takes an amendment: two-thirds of both houses, then three-fourths of the states.', cite: 'facts-branches.md 8.3, 8.4, 9.5 (Art. V)' },
    'lower-courts':    { text: 'Congress creates the lower federal courts.', cite: 'facts-branches.md 6.8 (Art. I, Sec. 8; Art. III, Sec. 1)' },
    'house-age':       { text: 'A member of the House must be at least 25.', cite: 'facts-branches.md 3.4 (Art. I, Sec. 2)' },
    'senate-age':      { text: 'A senator must be at least 30.', cite: 'facts-branches.md 3.6 (Art. I, Sec. 3)' },
    'president-age':   { text: 'A President must be at least 35.', cite: 'facts-branches.md 5.3 (Art. II, Sec. 1)' },
    'two-terms':       { text: 'No one can be elected President more than twice.', cite: 'facts-branches.md 5.2 (22nd Amendment)' },
    'house-term':      { text: 'House terms are two years, set by the Constitution.', cite: 'facts-branches.md 3.3 (Art. I, Sec. 2)' },
    'senate-term':     { text: 'Senate terms are six years, set by the Constitution.', cite: 'facts-branches.md 3.5 (Art. I, Sec. 3)' },
    pardon:            { text: 'The President can pardon federal crimes, but not an impeachment.', cite: 'facts-branches.md 5.6 (Art. II, Sec. 2)' },
    expel:             { text: 'Each chamber can expel a member with two-thirds.', cite: 'facts-branches.md 8.7 (Art. I, Sec. 5)' },
    unknown:           { text: 'Not in the powers table.', cite: null },
  };
  /* the rule each Front Page cheat (bank.front.json item.cheat) must break */
  const CHEATS = Object.freeze({
    fireJustice: 'life-tenure', courtMakesLaw: 'lawmaking', presidentStrikes: 'judicial-review', houseConfirms: 'senate-confirms',
    senateImpeaches: 'house-impeaches', presidentWar: 'war', courtNoCase: 'real-case', weakOverride: 'two-thirds-override',
    vpVotes: 'vp-tie', senatePassesFewer: 'majority', youngSenator: 'senate-age', youngPresident: 'president-age',
    youngRep: 'house-age', thirdTerm: 'two-terms', lawBeatsCourt: 'amendment', houseTerms: 'house-term',
  });

  /* ---------------- vote maths (integers) ---------------- */
  const isInt = v => Number.isInteger(v) && v >= 0;
  function need(v, name) { if (!isInt(v)) throw new Error(`civics: ${name} must be a whole number ≥ 0 (got ${v})`); return v; }
  const majorityNeeded = v => Math.floor(need(v, 'voting') / 2) + 1;
  const passes = (yes, no) => need(yes, 'yes') > need(no, 'no');
  const fails = (yes, no) => !passes(yes, no);
  const tie = (yes, no) => need(yes, 'yes') === need(no, 'no');
  const vpYes = vp => vp === true || vp === 'yes';
  const senatePasses = (yes, no, vp) => (tie(yes, no) ? vpYes(vp) : passes(yes, no));
  const twoThirdsNeeded = v => Math.floor((2 * need(v, 'voting') + 2) / 3);          // = ⌈2v/3⌉ exactly, no floats
  const overrides = (yes, no) => need(yes, 'yes') + need(no, 'no') > 0 && 3 * yes >= 2 * (yes + no);
  const overrideFails = (yes, no) => !overrides(yes, no);
  const twoThirds = overrides;

  /* a vote in a chamber: counts must fit the chamber, and a quorum must be voting (we only model votes with one) */
  function checkVote(chamber, yes, no, where) {
    need(yes, `${where} yes`); need(no, `${where} no`);
    const v = yes + no;
    if (v > CHAMBERS[chamber]) throw new Error(`civics: ${where}: ${yes} + ${no} = ${v} voters, but the ${chamber} has ${CHAMBERS[chamber]} seats`);
    if (v < QUORUM[chamber]) throw new Error(`civics: ${where}: ${v} voters is below the ${chamber}'s quorum (${QUORUM[chamber]})`);
  }
  const R = (rule) => ({ rule, cite: RULES[rule].cite });

  /* ---------------- journey ---------------- */
  const PRESIDENT = ['sign', 'veto', 'wait-in-session', 'wait-adjourned'];
  function journey(bill, params = {}) {
    if (!bill || typeof bill !== 'object') throw new Error('civics: journey(bill, params): no bill');
    const p = params, stages = [], path = [];
    const get = (k, where) => { if (!(k in p)) throw new Error(`civics: ${bill.id || 'bill'}: ${where} needs param "${k}"`); return p[k]; };
    const push = (stage, yes, no, result, rule) => { stages.push({ stage, yes, no, result, ...R(rule) }); path.push(stage + ':' + result); };
    const end = outcome => { stages.push({ stage: outcome, yes: null, no: null, result: outcome, rule: null, cite: null }); path.push(outcome); return { stages, path, outcome }; };
    const origin = bill.origin || 'house';
    if (!['house', 'senate'].includes(origin)) throw new Error(`civics: ${bill.id}: origin must be house or senate`);
    if (origin !== 'house' && (bill.tags || []).includes('revenue')) throw new Error(`civics: ${bill.id}: a revenue bill must start in the House (${RULES['revenue-house'].cite})`);
    const order = origin === 'house' ? ['house', 'senate'] : ['senate', 'house'];
    for (const ch of order) {
      const pre = ch === 'house' ? 'h' : 's', yes = get(pre + 'y', ch), no = get(pre + 'n', ch);
      checkVote(ch, yes, no, ch);
      if (ch === 'senate' && tie(yes, no)) {
        if (!bill.vp) throw new Error(`civics: ${bill.id}: a Senate tie (${yes}-${no}) needs "vp": "yes" | "no" on the bill`);
        const r = vpYes(bill.vp) ? 'tie-vp-yes' : 'tie-vp-no';
        push('senate', yes, no, r, 'vp-tie');
        if (!vpYes(bill.vp)) return end('dead');
        continue;
      }
      const ok = passes(yes, no);
      push(ch, yes, no, ok ? 'pass' : 'fail', 'majority');
      if (!ok) return end('dead');
    }
    const act = bill.president;
    if (!PRESIDENT.includes(act)) throw new Error(`civics: ${bill.id}: president must be one of ${PRESIDENT.join(', ')}`);
    push('president', null, null, act, act === 'wait-in-session' ? 'ten-days' : act === 'wait-adjourned' ? 'pocket-veto' : 'veto');
    if (act === 'wait-adjourned') return end('dead');                     // a pocket veto: no override possible (4.4)
    if (act === 'veto') {
      for (const [ch, pre] of [['house', 'oh'], ['senate', 'os']]) {
        const yes = get(pre + 'y', 'override-' + ch), no = get(pre + 'n', 'override-' + ch);
        checkVote(ch, yes, no, 'override-' + ch);
        const ok = overrides(yes, no);
        push('override-' + ch, yes, no, ok ? 'pass' : 'fail', 'two-thirds-override');
        if (!ok) return end('dead');
      }
    }
    const res = end('law');
    if (bill.court) {
      if (!['struck', 'upheld'].includes(bill.court)) throw new Error(`civics: ${bill.id}: court must be struck or upheld`);
      stages.push({ stage: 'court', yes: null, no: null, result: bill.court, ...R('judicial-review') });
      path.push('court:' + bill.court);
      res.outcome = bill.court === 'struck' ? 'struck' : 'law';
    }
    return res;
  }

  /* ---------------- the powers table ---------------- */
  const PARAM_KEYS = new Set(['actor', 'act']);
  function resolve(claim, params = {}) {
    const out = {};
    for (const [k, v] of Object.entries(claim || {})) out[k] = !PARAM_KEYS.has(k) && typeof v === 'string' && params && Object.prototype.hasOwnProperty.call(params, v) ? params[v] : v;
    return out;
  }
  const verdict = (ok, rule) => ({ ok: !!ok, rule, cite: RULES[rule].cite, text: RULES[rule].text });
  const hasVotes = c => c.yes !== undefined || c.no !== undefined;
  const votes = c => { need(c.yes, 'claim yes'); need(c.no, 'claim no'); return [c.yes, c.no]; };
  const chamberVoteOk = (ch, c) => { if (!hasVotes(c)) return true; const [y, n] = votes(c); return y + n <= CHAMBERS[ch] && y + n >= QUORUM[ch]; };
  const CHAMBER_OF = { house: 'house', senate: 'senate' };

  function allowed(claim0, params) {
    const c = resolve(claim0, params), A = c.actor, act = c.act;
    const unknown = { ok: false, rule: 'unknown', cite: null, text: RULES.unknown.text, unknown: true };
    // a vote that can't happen (more voters than seats, below quorum) is never allowed: report it as the claim's own rule
    const chamberCheck = rule => (CHAMBER_OF[A] && !chamberVoteOk(A, c) ? verdict(false, rule) : null);
    switch (act) {
      case 'makeLaw': case 'pass': {
        if (['court', 'president', 'vp', 'senator', 'representative'].includes(A)) return verdict(false, 'lawmaking');
        if (A === 'congress') return verdict(true, 'lawmaking');
        if (A === 'house' || A === 'senate') {
          const bad = chamberCheck('majority'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(true, 'majority');
          const [y, n] = votes(c);
          if (A === 'senate' && tie(y, n)) return verdict(c.vp !== undefined ? vpYes(c.vp) : false, 'vp-tie');
          return verdict(passes(y, n), 'majority');
        }
        return unknown;
      }
      case 'sign': case 'veto':
        return A === 'president' ? verdict(true, 'veto') : ['house', 'senate', 'congress', 'court', 'vp'].includes(A) ? verdict(false, 'veto') : unknown;
      case 'noAction':                 // the claim: the bill became law without the President's signature
        if (A !== 'president') return unknown;
        return c.inSession === true ? verdict(true, 'ten-days') : verdict(false, 'pocket-veto');
      case 'pocketVeto':
        if (A !== 'president') return unknown;
        return verdict(c.inSession === false, 'pocket-veto');
      case 'override': {
        if (['president', 'court', 'vp'].includes(A)) return verdict(false, 'two-thirds-override');
        if (c.pocket === true) return verdict(false, 'pocket-veto');
        if (A === 'congress') return verdict(true, 'two-thirds-override');
        if (A === 'house' || A === 'senate') {
          const bad = chamberCheck('two-thirds-override'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(true, 'two-thirds-override');
          const [y, n] = votes(c); return verdict(overrides(y, n), 'two-thirds-override');
        }
        return unknown;
      }
      case 'strikeDown': case 'review':
        if (A === 'court') return c.case === true ? verdict(true, 'judicial-review') : verdict(false, 'real-case');
        if (['president', 'congress', 'house', 'senate', 'vp'].includes(A)) return verdict(false, 'judicial-review');
        return unknown;
      case 'hearCase':
        if (A === 'court') return verdict(c.case === true, 'real-case');
        if (['president', 'congress', 'house', 'senate', 'vp'].includes(A)) return verdict(false, 'judicial-review');
        return unknown;
      case 'nominate':
        if (A === 'president') return verdict(true, 'nominate');
        if (['house', 'senate', 'congress', 'court', 'vp'].includes(A)) return verdict(false, 'nominate');
        return unknown;
      case 'confirm': {
        if (A === 'senate') {
          const bad = chamberCheck('senate-confirms'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(true, 'senate-confirms');
          const [y, n] = votes(c); return verdict(passes(y, n), 'senate-confirms');
        }
        if (['house', 'president', 'court', 'congress', 'vp'].includes(A)) return verdict(false, 'senate-confirms');
        return unknown;
      }
      case 'remove':                   // fire / remove a judge or justice
        if (!['judge', 'justice'].includes(c.office)) return unknown;
        if (A === 'senate') { const bad = chamberCheck('senate-tries'); if (bad) return bad; if (!hasVotes(c)) return verdict(false, 'senate-tries'); const [y, n] = votes(c); return verdict(twoThirds(y, n), 'senate-tries'); }
        if (['president', 'house', 'congress', 'court', 'vp'].includes(A)) return verdict(false, 'life-tenure');
        return unknown;
      case 'impeach': {
        if (A === 'house') {
          const bad = chamberCheck('house-impeaches'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(true, 'house-impeaches');
          const [y, n] = votes(c); return verdict(passes(y, n), 'house-impeaches');
        }
        if (['senate', 'president', 'court', 'congress', 'vp'].includes(A)) return verdict(false, 'house-impeaches');
        return unknown;
      }
      case 'convict': case 'try': {
        if (A === 'senate') {
          const bad = chamberCheck('senate-tries'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(act === 'try', 'senate-tries');
          const [y, n] = votes(c); return verdict(twoThirds(y, n), 'senate-tries');
        }
        if (['house', 'president', 'court', 'congress', 'vp'].includes(A)) return verdict(false, 'senate-tries');
        return unknown;
      }
      case 'tiebreak': case 'vote':
        if (A === 'vp') { const [y, n] = votes(c); return verdict(tie(y, n) && y + n <= CHAMBERS.senate, 'vp-tie'); }
        if (['president', 'court'].includes(A)) return verdict(false, 'vp-tie');
        return unknown;
      case 'declareWar':
        if (A === 'congress') return verdict(true, 'war');
        if (['president', 'vp', 'court', 'house', 'senate'].includes(A)) return verdict(false, 'war');
        return unknown;
      case 'command':
        if (A === 'president') return verdict(true, 'commander');
        if (['congress', 'house', 'senate', 'court', 'vp'].includes(A)) return verdict(false, 'commander');
        return unknown;
      case 'budget': case 'spend': case 'tax':
        if (A === 'congress') return verdict(true, 'purse');
        if (['president', 'court', 'vp'].includes(A)) return verdict(false, 'purse');
        return unknown;
      case 'treatyConsent': case 'ratify': {
        if (A === 'senate') {
          const bad = chamberCheck('treaty'); if (bad) return bad;
          if (!hasVotes(c)) return verdict(true, 'treaty');
          const [y, n] = votes(c); return verdict(twoThirds(y, n), 'treaty');
        }
        if (['house', 'congress', 'court', 'vp'].includes(A)) return verdict(false, 'treaty');
        return unknown;
      }
      case 'proposeAmendment':
        if (A === 'congress') { if (!hasVotes(c)) return verdict(true, 'amendment'); const [y, n] = votes(c); return verdict(twoThirds(y, n), 'amendment'); }
        if (['president', 'court', 'vp'].includes(A)) return verdict(false, 'amendment');
        return unknown;
      case 'overruleConstitution':     // undo a ruling on the Constitution
        if (A === 'congress') return verdict(c.by === 'amendment', 'amendment');
        if (['president', 'house', 'senate', 'vp'].includes(A)) return verdict(false, 'amendment');
        return unknown;
      case 'createCourt':
        if (A === 'congress') return verdict(true, 'lower-courts');
        if (['president', 'court', 'vp'].includes(A)) return verdict(false, 'lower-courts');
        return unknown;
      case 'serve': {
        const office = { representative: 'representative', senator: 'senator', president: 'president' }[A];
        if (!office) return unknown;
        need(c.age, 'claim age');
        const ageRule = { representative: 'house-age', senator: 'senate-age', president: 'president-age' }[office];
        if (c.age < AGE[office]) return verdict(false, ageRule);
        if (office === 'president' && c.term !== undefined) { need(c.term, 'claim term'); if (c.term < 1 || c.term > MAX_PRESIDENT_TERMS) return verdict(false, 'two-terms'); return verdict(true, 'two-terms'); }
        return verdict(true, ageRule);
      }
      case 'setTerm':                  // a chamber changing its own term length by a vote
        if (A === 'house') { need(c.years, 'claim years'); return verdict(c.years === TERM.house, 'house-term'); }
        if (A === 'senate') { need(c.years, 'claim years'); return verdict(c.years === TERM.senate, 'senate-term'); }
        return unknown;
      case 'pardon':
        if (A === 'president') return verdict(c.impeachment !== true, 'pardon');
        return unknown;
      case 'expel':
        if (A === 'house' || A === 'senate') { const bad = chamberCheck('expel'); if (bad) return bad; if (!hasVotes(c)) return verdict(true, 'expel'); const [y, n] = votes(c); return verdict(twoThirds(y, n), 'expel'); }
        return unknown;
      default: return unknown;
    }
  }

  /* ---------------- instances through the shared bank (sampler, evaluator, filler, shuffles) ---------------- */
  const asRng = r => { const Bk = B(); return typeof r === 'function' ? r : Bk.rng(r == null ? Bk.newSeed() : r); };
  /* the bank item an ask becomes: a numeric ask = a template (formula + distractors), a fixed ask = a scenario item
     ("cheat": the bill id marks it as lesson data), both over the BILL's params + where (so both sample the same draw) */
  function askItem(bill, ask, i, params) {
    const P = params ? Object.fromEntries(Object.entries(params).map(([k, v]) => [k, { choose: [v] }])) : bill.params;
    const base = { id: `${bill.id}.${i + 1}`, tags: (bill.tags || []).slice(), misconception: bill.misconception, q: ask.q, why: ask.why, params: P };
    if (!params && bill.where) base.where = bill.where;
    if (ask.formula) return { ...base, formula: ask.formula, distractors: ask.distractors, format: ask.format || 'int', explain: ask.explain || '', answer: 'choice' };
    return { ...base, options: ask.options, correct: ask.correct, explain: ask.explain || ask.options[ask.correct], cheat: bill.id };
  }
  function sample(item, r) {
    const Bk = B();
    if (!item.params) return {};
    const s = Bk.sampleParams(item, r);
    if (!s) throw new Error(`civics: ${item.id}: no params satisfy "where" in 400 tries`);
    return s.params;
  }
  /* one bill: its params drawn once, its journey, its three asks as bank instances over those params */
  function bill(item, r0, { slots } = {}) {
    const Bk = B(), r = asRng(r0), params = sample(item, r);
    const j = journey(item, params);
    const asks = (item.asks || []).map((a, i) => {
      const inst = Bk.instantiate(askItem(item, a, i, params), r, { slot: slots ? slots(a) : undefined });
      inst.at = a.at; inst.ask = a;
      inst.truth = a.truth ? !!Bk.eval(a.truth, params) : null;
      return inst;
    });
    return { id: item.id, key: Bk.keyOf(item, params), title: item.bill, level: item.level || 1, tags: item.tags.slice(), misconception: item.misconception || null,
      params, journey: j, path: j.path, asks, item };
  }
  /* least-used option slot, like Bank.draw: the right answers spread over the slots */
  function spreader(r) {
    const used = [0, 0, 0, 0];
    return a => { const n = a.formula ? Math.min(4, Object.keys(a.distractors || {}).length + 1) : a.options.length; const lo = Math.min(...used.slice(0, n)), c = []; for (let i = 0; i < n; i++) if (used[i] === lo) c.push(i); const s = r.pick(c); used[s]++; return s; };
  }
  function drawBills(json, { seed, avoid = [] } = {}) {
    const Bk = B();
    const s = seed == null ? Bk.newSeed() : seed, r = Bk.rng(s), d = json.draw || {}, items = json.items || [];
    const n = Math.min(d.n || 4, items.length), avoidSet = new Set(d.avoidRepeat === false ? [] : avoid);
    const chosen = [], taken = new Set();
    const choose = cands => {
      const fresh = cands.filter(it => !taken.has(it.id) && !avoidSet.has(it.id)), any = fresh.length ? fresh : cands.filter(it => !taken.has(it.id));
      if (!any.length) return false;
      const it = r.pick(any); chosen.push(it); taken.add(it.id); return true;
    };
    for (const tag of r.shuffle(d.require || [])) { if (chosen.length >= n) break; if (!chosen.some(it => it.tags.includes(tag))) choose(items.filter(it => it.tags.includes(tag))); }
    while (chosen.length < n && choose(items));
    // easiest first (level), ties in draw order
    const ordered = chosen.map((it, k) => ({ it, k })).sort((a, b) => (a.it.level || 1) - (b.it.level || 1) || a.k - b.k).map(x => x.it);
    const slots = spreader(r);
    return { seed: s, bills: ordered.map(it => bill(it, r, { slots })) };
  }

  function headline(item, r0) {
    const Bk = B(), r = asRng(r0), params = sample(item, r);
    const claim = resolve(item.claim, params), v = allowed(claim);
    const out = { id: item.id, key: Bk.keyOf({ id: item.id, params: item.params || {} }, item.params ? params : null), kind: item.kind, cheat: item.cheat || null,
      level: item.level || 1, tags: item.tags.slice(), misconception: item.misconception || null, params,
      headline: Bk.fill(item.headline, params), small: Bk.fill(item.small || '', params), claim, verdict: v, card: null, fix: '', item };
    if (item.kind === 'cheat') {
      const fixed = Object.fromEntries(Object.entries(params).map(([k, x]) => [k, { choose: [x] }]));
      out.card = Bk.instantiate({ id: item.id, tags: item.tags, q: item.q, options: item.options, correct: item.correct, why: item.why,
        explain: item.fix || '', params: fixed, cheat: item.cheat }, r);
      out.fix = Bk.fill(item.fix || '', params);
    }
    return out;
  }
  /* the same headline text before filling = the same "template" (a legit and a cheat item may share one: two ages) */
  const tplOf = it => String(it.headline || '').replace(/\{[^{}]*\}/g, '#');
  function drawFront(json, { seed, avoid = [] } = {}) {
    const Bk = B();
    const s = seed == null ? Bk.newSeed() : seed, r = Bk.rng(s), d = json.draw || {}, items = json.items || [];
    const nC = d.cheats || 5, nL = d.legit || 13, avoidSet = new Set(d.avoidRepeat === false ? [] : avoid);
    const U = d.unique || 'cheat';
    const fresh = list => { const f = list.filter(it => !avoidSet.has(it.id)); return f.length ? f : list; };
    // cheats: unique by `unique`, fresh first
    const cheats = [], usedU = new Set();
    for (let k = 0; k < nC; k++) {
      const cands = items.filter(it => it.kind === 'cheat' && !cheats.includes(it) && !usedU.has(JSON.stringify(it[U])));
      if (!cands.length) throw new Error(`civics: drawFront: only ${cheats.length} distinct cheats for ${nC}`);
      const pool = fresh(cands), it = r.pick(pool); cheats.push(it); usedU.add(JSON.stringify(it[U]));
    }
    const cheatsOrdered = cheats.map((it, k) => ({ it, k })).sort((a, b) => (a.it.level || 1) - (b.it.level || 1) || a.k - b.k).map(x => x.it);
    // legit: nL distinct items, fresh first
    const legit = [];
    for (let k = 0; k < nL; k++) {
      const cands = items.filter(it => it.kind === 'legit' && !legit.includes(it));
      if (!cands.length) throw new Error(`civics: drawFront: only ${legit.length} legit items for ${nL}`);
      legit.push(r.pick(fresh(cands)));
    }
    // pages: the all-legit page anywhere but first; each cheat page gets 2 legit, the legit page 3; no shared template on a page
    const nPages = cheatsOrdered.length + 1, allLegitAt = 1 + r.int(nPages - 1);
    const slotsPer = [];
    let ci = 0;
    for (let p = 0; p < nPages; p++) slotsPer.push(p === allLegitAt ? { cheat: null, legit: [] , want: 3 } : { cheat: cheatsOrdered[ci++], legit: [], want: 2 });
    if (slotsPer.reduce((a, pg) => a + pg.want, 0) !== legit.length) throw new Error(`civics: drawFront: ${legit.length} legit items don't fill ${nPages} pages`);
    // assign with backtracking (tiny search): legit items in random order, first page with room and no template clash
    const order = r.shuffle(legit);
    const clash = (pg, it) => (pg.cheat && tplOf(pg.cheat) === tplOf(it)) || pg.legit.some(x => tplOf(x) === tplOf(it));
    const place = k => {
      if (k === order.length) return true;
      for (const pg of slotsPer) if (pg.legit.length < pg.want && !clash(pg, order[k])) { pg.legit.push(order[k]); if (place(k + 1)) return true; pg.legit.pop(); }
      return false;
    };
    if (!place(0)) throw new Error('civics: drawFront: no page layout keeps same-template headlines apart');
    const pages = slotsPer.map(pg => {
      const list = r.shuffle([...(pg.cheat ? [pg.cheat] : []), ...pg.legit]).map(it => headline(it, r));
      return { cheat: list.find(h => h.kind === 'cheat') || null, headlines: list, answer: list.findIndex(h => h.kind === 'cheat') };
    });
    return { seed: s, pages, cheats: cheatsOrdered.map(it => it.id), legit: legit.map(it => it.id) };
  }

  const Civics = {
    CHAMBERS, QUORUM, AGE, TERM, MAX_PRESIDENT_TERMS, VETOES, RULES, CHEATS,
    majorityNeeded, passes, fails, tie, senatePasses, twoThirdsNeeded, overrides, overrideFails, twoThirds,
    journey, resolve, allowed,
    askItem, bill, drawBills, headline, drawFront, templateOf: tplOf,
  };
  root.Civics = Civics;
  const Bk = B();
  if (Bk && typeof Bk.register === 'function') Bk.register('civics', Civics);
  if (typeof module !== 'undefined' && module.exports) module.exports = Civics;
})(typeof window !== 'undefined' ? window : globalThis);
